import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Anchor, AlertTriangle, Clock, Ship, Filter,
  RefreshCw, Loader2, ArrowRight, CheckCircle
} from 'lucide-react';

const SEVERITY_STYLES = {
  critical: 'border-red-300 text-red-700 bg-red-50',
  warning: 'border-amber-300 text-amber-700 bg-amber-50',
  advisory: 'border-blue-300 text-blue-700 bg-blue-50',
};

export default function BerthSchedulePage() {
  const [schedule, setSchedule] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async (terminal) => {
    try {
      const params = terminal ? `?terminal=${encodeURIComponent(terminal)}` : '';
      const [s, c, t] = await Promise.all([
        api.get(`/scheduling/berth-schedule${params}`),
        api.get(`/scheduling/conflicts${params}`),
        api.get('/scheduling/terminals'),
      ]);
      setSchedule(s.data);
      setConflicts(c.data);
      setTerminals(t.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const handleTerminalFilter = (terminalName) => {
    const next = terminalName === selectedTerminal ? null : terminalName;
    setSelectedTerminal(next);
    setRefreshing(true);
    loadAll(next);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadAll(selectedTerminal);
  };

  const formatDateTime = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const scheduleByTerminal = schedule.reduce((acc, s) => {
    const t = s.terminal_name || 'Unknown';
    if (!acc[t]) acc[t] = [];
    acc[t].push(s);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="p-6 md:p-8 space-y-6" data-testid="berth-schedule-loading">
        <Skeleton className="h-8 w-56" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="berth-schedule-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Berth Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">Terminal berth scheduling & conflict detection</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm text-xs">{schedule.length} bookings</Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-schedule-button"
            className="rounded-sm text-xs border-slate-200"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Total Bookings</p>
                <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{schedule.length}</p>
                <p className="text-xs text-slate-400 mt-1">{Object.keys(scheduleByTerminal).length} terminals</p>
              </div>
              <div className="w-9 h-9 rounded-sm bg-[#0A2463] flex items-center justify-center">
                <Anchor className="h-4 w-4 text-white stroke-[1.5px]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Active Conflicts</p>
                <p className="font-mono text-2xl font-bold text-slate-900 mt-1">{conflicts.length}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {conflicts.filter(c => c.severity === 'critical').length} critical
                </p>
              </div>
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${conflicts.length > 0 ? 'bg-[#E11D48]' : 'bg-[#16A34A]'}`}>
                {conflicts.length > 0 ? <AlertTriangle className="h-4 w-4 text-white stroke-[1.5px]" /> : <CheckCircle className="h-4 w-4 text-white stroke-[1.5px]" />}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Scheduled Vessels</p>
                <p className="font-mono text-2xl font-bold text-slate-900 mt-1">
                  {new Set(schedule.map(s => s.vessel_name).filter(Boolean)).size}
                </p>
                <p className="text-xs text-slate-400 mt-1">Unique vessels</p>
              </div>
              <div className="w-9 h-9 rounded-sm bg-[#007AFF] flex items-center justify-center">
                <Ship className="h-4 w-4 text-white stroke-[1.5px]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Terminal Filter */}
      <div className="flex flex-wrap gap-1.5">
        <Button
          variant={selectedTerminal === null ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleTerminalFilter(null)}
          className={`rounded-sm text-xs ${selectedTerminal === null ? 'bg-[#0A2463] text-white' : 'border-slate-200'}`}
          data-testid="filter-all-terminals"
        >
          <Filter className="h-3 w-3 mr-1" />All Terminals
        </Button>
        {terminals.map(t => (
          <Button
            key={t.name}
            variant={selectedTerminal === t.name ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTerminalFilter(t.name)}
            data-testid={`filter-terminal-${t.name.replace(/\s/g, '-')}`}
            className={`rounded-sm text-xs ${selectedTerminal === t.name ? 'bg-[#0A2463] text-white' : 'border-slate-200'}`}
          >
            {t.name}
          </Button>
        ))}
      </div>

      {/* Conflicts */}
      {conflicts.length > 0 && (
        <Card className="bg-white border border-red-200 rounded-sm shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-red-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />Scheduling Conflicts ({conflicts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="conflicts-table">
              <thead>
                <tr className="border-b border-red-100">
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Terminal</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel A</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel B</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Severity</th>
                  <th className="text-right text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Overlap</th>
                  <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {conflicts.map(c => (
                  <tr key={c.id} className="border-b border-red-50 hover:bg-red-50/30">
                    <td className="px-5 py-2 text-xs font-medium text-slate-800">{c.terminal}</td>
                    <td className="px-5 py-2 text-xs text-slate-600">{c.vessel_a}</td>
                    <td className="px-5 py-2 text-xs text-slate-600">{c.vessel_b}</td>
                    <td className="px-5 py-2">
                      <Badge variant="outline" className={`rounded-sm text-[9px] uppercase tracking-wider ${SEVERITY_STYLES[c.severity] || ''}`}>
                        {c.severity}
                      </Badge>
                    </td>
                    <td className="px-5 py-2 text-right font-mono text-xs text-red-600 font-bold">{c.overlap_hours}h</td>
                    <td className="px-5 py-2 text-xs text-slate-600 max-w-[200px] truncate">{c.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Schedule by Terminal */}
      {Object.keys(scheduleByTerminal).length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
          <CardContent className="py-24 text-center">
            <Anchor className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
            <p className="mt-4 text-sm text-slate-400">No berth schedules found</p>
            <p className="text-xs text-slate-300 mt-1">Berth schedule data will populate as vessels are tracked</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(scheduleByTerminal).map(([terminal, bookings]) => (
          <Card key={terminal} className="bg-white border border-slate-200 rounded-sm shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Anchor className="h-4 w-4 text-[#007AFF]" />{terminal}
                <Badge variant="outline" className="rounded-sm text-[9px] ml-1">{bookings.length} slots</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid={`schedule-table-${terminal.replace(/\s/g, '-')}`}>
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Vessel</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Berth</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Arrival</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Departure</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Status</th>
                    <th className="text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-5 py-2">Trade ID</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-5 py-2 text-xs font-medium text-slate-800">{b.vessel_name || '—'}</td>
                      <td className="px-5 py-2 text-xs font-mono text-slate-600">{b.berth_id || '—'}</td>
                      <td className="px-5 py-2 text-xs font-mono text-slate-600">{formatDateTime(b.scheduled_arrival)}</td>
                      <td className="px-5 py-2 text-xs font-mono text-slate-600">{formatDateTime(b.scheduled_departure)}</td>
                      <td className="px-5 py-2">
                        <Badge variant="outline" className={`rounded-sm text-[9px] uppercase tracking-wider ${
                          b.status === 'scheduled' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                          b.status === 'arrived' ? 'border-green-300 text-green-700 bg-green-50' :
                          b.status === 'departed' ? 'border-slate-200 text-slate-500' :
                          'border-slate-200 text-slate-500'
                        }`}>
                          {b.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-2 text-xs font-mono text-slate-400">{b.trade_id || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
